import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.trubonn.com.au';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

interface SEOProps {
  /** Page title. Rendered as-is — include the "| Trubonn" suffix where it reads naturally. */
  title: string;
  /** Meta description, ideally under ~160 characters. */
  description: string;
  /** Path only, e.g. "/pricing" or "/blog/my-post-slug". Defaults to the homepage. */
  path?: string;
  /** Absolute image URL for social previews. Defaults to the site's default OG image. */
  image?: string;
}

/**
 * Per-route SEO tags for this client-rendered (Create React App) site.
 *
 * public/index.html only sets one static title/description/canonical for the whole
 * app, which means every route — including every blog post — was serving identical
 * metadata and, worse, a canonical tag that pointed every page back at the homepage.
 * Render this once near the top of each routed page's JSX to override those tags
 * for that specific route. react-helmet-async updates the existing <title>,
 * <meta name="description">, and <link rel="canonical"> tags in document.head in
 * place, so this works without needing server-side rendering.
 */
export default function SEO({ title, description, path = '/', image = DEFAULT_IMAGE }: SEOProps) {
  const url = path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
